import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { acceptReferral, fetchMyReferralInfo, type MyReferralInfo } from "../api/referrals.api";

const SHARE_MESSAGE =
  "Join me on Stellex Tycoon — build your virtual exchange and win real prizes!";

/** Telegram WebApp's openLink/openTelegramLink work for external URLs from
 * inside the Telegram in-app browser. Outside Telegram (e.g. desktop browser
 * test) we fall back to window.open. */
function openExternal(url: string, kind: "telegram" | "external" = "external") {
  const tg = (
    window as {
      Telegram?: {
        WebApp?: {
          openLink?: (url: string) => void;
          openTelegramLink?: (url: string) => void;
        };
      };
    }
  ).Telegram?.WebApp;
  if (kind === "telegram" && tg?.openTelegramLink) {
    tg.openTelegramLink(url);
    return;
  }
  if (tg?.openLink) {
    tg.openLink(url);
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

/** Clipboard write with a textarea fallback for webviews where
 * navigator.clipboard is gated by permissions or unavailable. */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy fallback
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

type ApplyState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success"; message: string }
  | { kind: "error"; message: string };

export function ReferralsPanel() {
  const [info, setInfo] = useState<MyReferralInfo | null>(null);
  const [copied, setCopied] = useState(false);
  const linkRef = useRef<HTMLInputElement | null>(null);
  const [enteredCode, setEnteredCode] = useState("");
  const [apply, setApply] = useState<ApplyState>({ kind: "idle" });

  const handleApplyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = enteredCode.trim();
    if (!code) return;
    setApply({ kind: "submitting" });
    try {
      const res = await acceptReferral(code);
      if (res.accepted) {
        setApply({ kind: "success", message: "You're linked! +500 bonus tokens credited." });
        // Refresh stats since the inviter row now exists upstream.
        const fresh = await fetchMyReferralInfo();
        setInfo(fresh);
      } else if (res.reason === "already_referred") {
        setApply({ kind: "error", message: "You already have an inviter — codes can only be applied once." });
      } else {
        setApply({ kind: "error", message: res.reason ?? "Could not apply that code." });
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message ??
        (err as Error).message;
      const text = Array.isArray(msg) ? msg.join(", ") : msg;
      setApply({ kind: "error", message: text ?? "Invalid code." });
    }
  };

  useEffect(() => {
    fetchMyReferralInfo().then(setInfo).catch(() => {
      // Silently fail — referrals not critical to render
    });
  }, []);

  const handleCopy = async () => {
    if (!info) return;
    const ok = await copyToClipboard(info.inviteLink);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } else if (linkRef.current) {
      // Last resort — select the input so the user can copy manually.
      linkRef.current.focus();
      linkRef.current.select();
    }
  };

  const shareTelegram = () => {
    if (!info) return;
    const url = `https://t.me/share/url?url=${encodeURIComponent(info.inviteLink)}&text=${encodeURIComponent(SHARE_MESSAGE)}`;
    openExternal(url, "telegram");
  };

  const shareWhatsApp = () => {
    if (!info) return;
    const url = `https://wa.me/?text=${encodeURIComponent(`${SHARE_MESSAGE} ${info.inviteLink}`)}`;
    openExternal(url);
  };

  const shareTwitter = () => {
    if (!info) return;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_MESSAGE)}&url=${encodeURIComponent(info.inviteLink)}`;
    openExternal(url);
  };

  const shareFacebook = () => {
    if (!info) return;
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(info.inviteLink)}&quote=${encodeURIComponent(SHARE_MESSAGE)}`;
    openExternal(url);
  };

  /** Native OS share sheet — picks up Instagram, Snapchat, SMS, email, etc.
   * Mobile-only; falls back gracefully if unavailable. */
  const shareNative = async () => {
    if (!info) return;
    if (!navigator.share) {
      // No native share — best fallback is to copy.
      await handleCopy();
      return;
    }
    try {
      await navigator.share({
        title: "Stellex Tycoon",
        text: SHARE_MESSAGE,
        url: info.inviteLink,
      });
    } catch {
      // User dismissed the share sheet — no-op.
    }
  };

  const hasNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  if (!info) {
    return (
      <section className="referrals-panel">
        <h2>Invite Friends</h2>
        <div className="loading">Loading…</div>
      </section>
    );
  }

  // Format basis points as percent for display: 1250 → "12.5%"
  const fmtBps = (bps: number) => {
    const pct = bps / 100;
    return Number.isInteger(pct) ? `${pct}%` : `${pct.toFixed(2).replace(/\.?0+$/, "")}%`;
  };

  return (
    <section className="referrals-panel">
      <h2>Invite Friends</h2>

      <div className="referral-stats-grid">
        <div className="stat-card">
          <div className="stat-value">{info.directInvites}</div>
          <div className="stat-label">Direct (T1)</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{info.tier2Invites}</div>
          <div className="stat-label">2nd tier</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{info.tier3Invites}</div>
          <div className="stat-label">3rd tier</div>
        </div>
        <div className="stat-card highlight">
          <div className="stat-value">+{Number(info.totalEarnedTokens).toLocaleString()}</div>
          <div className="stat-label">Earned</div>
        </div>
      </div>

      {/* Level progression — current level + table of all 5 with current row
       * highlighted. Cuts grow with directInvites count. */}
      <div className="level-card">
        <div className="level-header">
          <div>
            <div className="level-title">
              <span className="level-badge">L{info.currentLevel}</span>
              <span className="level-rates">
                {fmtBps(info.currentTier1Bps)} · {fmtBps(info.currentTier2Bps)} · {fmtBps(info.currentTier3Bps)}
              </span>
            </div>
            {info.nextLevel != null && info.nextLevelInvitesNeeded != null ? (
              <div className="level-next">
                {info.nextLevelInvitesNeeded} more invite
                {info.nextLevelInvitesNeeded === 1 ? "" : "s"} → L{info.nextLevel}
              </div>
            ) : (
              <div className="level-next max">Max level — top rates locked in.</div>
            )}
          </div>
        </div>

        {/* Progress bar to next level */}
        {info.nextLevel != null && (() => {
          const nextLvl = info.levels.find((l) => l.level === info.nextLevel);
          const prevLvl = info.levels.find((l) => l.level === info.currentLevel);
          if (!nextLvl || !prevLvl) return null;
          const range = nextLvl.minDirectInvites - prevLvl.minDirectInvites;
          const done = info.directInvites - prevLvl.minDirectInvites;
          const pct = range > 0 ? Math.max(0, Math.min(100, (done / range) * 100)) : 0;
          return (
            <div className="level-progress">
              <motion.div
                className="level-progress-fill"
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ duration: 0.6 }}
              />
            </div>
          );
        })()}

        {/* Level ladder — read-only table */}
        <div className="level-table">
          <div className="level-row level-row-head">
            <span>Lvl</span>
            <span>Invites</span>
            <span>T1</span>
            <span>T2</span>
            <span>T3</span>
          </div>
          {info.levels.map((l) => {
            const isCurrent = l.level === info.currentLevel;
            return (
              <div
                key={l.level}
                className={`level-row ${isCurrent ? "current" : ""}`}
              >
                <span className="level-row-num">L{l.level}</span>
                <span>{l.minDirectInvites}+</span>
                <span>{fmtBps(l.tier1Bps)}</span>
                <span>{fmtBps(l.tier2Bps)}</span>
                <span>{fmtBps(l.tier3Bps)}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="referral-link-card">
        <div className="referral-code">{info.code}</div>

        {/* Read-only input keeps the link selectable on long-press / right-click,
            and gives us a fallback target to .select() when clipboard fails. */}
        <div className="referral-link-row">
          <input
            ref={linkRef}
            className="referral-link-input"
            value={info.inviteLink}
            readOnly
            onFocus={(e) => e.currentTarget.select()}
          />
          <button
            className={`copy-btn ${copied ? "copied" : ""}`}
            onClick={handleCopy}
            type="button"
            aria-label="Copy invite link"
          >
            <motion.span
              key={copied ? "ok" : "copy"}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              {copied ? "✓ Copied" : "📋 Copy"}
            </motion.span>
          </button>
        </div>

        <div className="share-grid">
          {hasNativeShare && (
            <button className="share-chip share-native" onClick={shareNative} type="button">
              <span className="share-icon">📤</span>
              <span>Share…</span>
            </button>
          )}
          <button className="share-chip share-telegram" onClick={shareTelegram} type="button">
            <span className="share-icon">✈️</span>
            <span>Telegram</span>
          </button>
          <button className="share-chip share-whatsapp" onClick={shareWhatsApp} type="button">
            <span className="share-icon">💬</span>
            <span>WhatsApp</span>
          </button>
          <button className="share-chip share-twitter" onClick={shareTwitter} type="button">
            <span className="share-icon">𝕏</span>
            <span>X / Twitter</span>
          </button>
          <button className="share-chip share-facebook" onClick={shareFacebook} type="button">
            <span className="share-icon">f</span>
            <span>Facebook</span>
          </button>
        </div>
      </div>

      {/* Manual code entry — only available within 24h of joining + only
       * if no inviter is already locked in. After the window closes the
       * card hides entirely so the surface stays uncluttered. */}
      {info.canApplyCode && (
        <div className="apply-code-card">
          <div className="apply-code-header">
            <div className="apply-code-titles">
              <span>Got an invite code?</span>
              <span className="muted">Paste it once to link with your inviter</span>
            </div>
            {info.applyCodeExpiresAt && (
              <ApplyCodeCountdown expiresAt={info.applyCodeExpiresAt} />
            )}
          </div>
          <form onSubmit={handleApplyCode} className="apply-code-form">
            <input
              value={enteredCode}
              onChange={(e) => {
                setEnteredCode(e.target.value.toUpperCase());
                if (apply.kind !== "idle") setApply({ kind: "idle" });
              }}
              placeholder="STX-XXXXXX"
              maxLength={20}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              disabled={apply.kind === "submitting"}
            />
            <button
              type="submit"
              disabled={!enteredCode.trim() || apply.kind === "submitting"}
            >
              {apply.kind === "submitting" ? "Applying…" : "Apply"}
            </button>
          </form>
          {apply.kind === "success" && (
            <div className="apply-code-msg success">{apply.message}</div>
          )}
          {apply.kind === "error" && (
            <div className="apply-code-msg error">{apply.message}</div>
          )}
          <p className="apply-code-fineprint">
            Codes can only be applied within <strong>24 hours</strong> of joining.
          </p>
        </div>
      )}

      <div className="referral-rates">
        <p>
          <strong>+500 tokens</strong> the moment they join. You earn{" "}
          <strong>{fmtBps(info.currentTier1Bps)}</strong> of their tokens,{" "}
          <strong>{fmtBps(info.currentTier2Bps)}</strong> of their friends',{" "}
          <strong>{fmtBps(info.currentTier3Bps)}</strong> of their friends' friends' — forever.
          {info.nextLevel != null && info.nextLevelInvitesNeeded != null && (
            <>
              {" "}
              Invite <strong>{info.nextLevelInvitesNeeded}</strong> more to unlock L{info.nextLevel} rates.
            </>
          )}
        </p>
        {info.todayInviteCount > 0 && (
          <p className="muted">
            You've invited <strong>{info.todayInviteCount}</strong> today
            {info.todayInviteCount >= 3 && " — bonus quest unlocked!"}
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * Tiny live countdown chip — "Closes in 23h 41m". Updates every minute,
 * which is more than enough resolution for a 24h window. Shows "Closes in
 * <1m" once we're inside the final minute (server still authoritative).
 */
function ApplyCodeCountdown({ expiresAt }: { expiresAt: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  const expiresMs = new Date(expiresAt).getTime();
  const remainingMs = Math.max(0, expiresMs - now);
  const hours = Math.floor(remainingMs / 3_600_000);
  const minutes = Math.floor((remainingMs % 3_600_000) / 60_000);
  const label =
    remainingMs === 0
      ? "Closed"
      : hours > 0
        ? `Closes in ${hours}h ${minutes}m`
        : minutes > 0
          ? `Closes in ${minutes}m`
          : "Closes in <1m";
  return <span className={`apply-countdown ${hours === 0 ? "urgent" : ""}`}>{label}</span>;
}
